export const ANALYSIS_PLOT_MIN_WIDTH = 640;

const PANEL_MIN_WIDTH = {
  lap3d: 360,
  headtohead: 360,
  splits: 320,
};

const PLOT_PANEL_IDS = new Set([
  'speed',
  'gTrace',
  'delta',
  'power',
  'frictionCircle',
  'scatterPlot',
  'trackDifference',
]);

export function getPanelMinWidth(id) {
  if (PLOT_PANEL_IDS.has(id) || id?.startsWith('ch_') || id?.startsWith('fml_')) {
    return ANALYSIS_PLOT_MIN_WIDTH;
  }
  return PANEL_MIN_WIDTH[id] ?? 240;
}

export function normalizeWindowRect(layout, minWidth, minHeight) {
  const isFiniteNum = (value) => typeof value === 'number' && Number.isFinite(value);
  return {
    x: isFiniteNum(layout.x) ? layout.x : 200,
    y: isFiniteNum(layout.y) ? layout.y : 90,
    width: isFiniteNum(layout.width)
      ? Math.max(minWidth, layout.width)
      : Math.max(minWidth, 400),
    height: isFiniteNum(layout.height) ? layout.height : Math.max(minHeight, 300),
  };
}

export function arrangePanelRects(panelIds, workspaceBounds) {
  const ids = [...new Set((panelIds || []).filter(Boolean))];
  if (!ids.length) return {};

  const pad = 4;
  const left   = Math.round(workspaceBounds.left) + pad;
  const top    = Math.round(workspaceBounds.top)  + pad;
  const availW = Math.max(1, Math.round(workspaceBounds.width)  - pad * 2);
  const availH = Math.max(1, Math.round(workspaceBounds.height) - pad * 2);

  // Prefer cells close to the wide shape of analysis charts, while keeping
  // enough width for the widest open panel whenever the viewport permits.
  const widestMinimum = Math.max(240, ...ids.map(getPanelMinWidth));
  const maxColumns = Math.max(1, Math.min(
    ids.length,
    Math.floor((availW + pad) / (widestMinimum + pad)),
  ));
  let columns = 1;
  let bestScore = Infinity;
  for (let candidate = 1; candidate <= maxColumns; candidate += 1) {
    const rows = Math.ceil(ids.length / candidate);
    const cellAspect = (availW / candidate) / (availH / rows);
    const emptyCells = rows * candidate - ids.length;
    const cellHeight = (availH - pad * (rows - 1)) / rows;
    const heightPenalty = cellHeight < 120 ? (120 / Math.max(1, cellHeight) - 1) * 10 : 0;
    const score = Math.abs(Math.log(cellAspect / 1.6)) + emptyCells * 0.08 + heightPenalty;
    if (score < bestScore) {
      bestScore = score;
      columns = candidate;
    }
  }

  const rows = Math.ceil(ids.length / columns);
  const cellW = (availW - pad * (columns - 1)) / columns;
  const cellH = (availH - pad * (rows - 1)) / rows;
  return Object.fromEntries(ids.map((id, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const x = Math.round(left + column * (cellW + pad));
    const y = Math.round(top + row * (cellH + pad));
    const right = column === columns - 1
      ? left + availW
      : Math.round(left + (column + 1) * cellW + column * pad);
    const bottom = row === rows - 1
      ? top + availH
      : Math.round(top + (row + 1) * cellH + row * pad);
    return [id, { x, y, width: right - x, height: bottom - y }];
  }));
}
